import React from 'react';
import styled from 'styled-components';
import {
  brand,
  motion,
  semantic,
  space,
  support,
  surface,
  typeRole,
  weight,
} from '@repo/design-tokens';
import { Button, Icon, SIDE_TABS_GLASS_DIVIDER, SideTab, SideTabs, Tag } from '@repo/design-system';
import type { IconName } from '@repo/design-system';
import { visuallyHidden } from '@repo/ui';
import type { StepId } from './OnboardingPage';
import { NAV_WIDTH_COLLAPSED, NAV_WIDTH_EXPANDED } from './glassLayout';

export interface OnboardingNavItem {
  /** Which step this is. The list reports it on selection and matches it against `current`. */
  id: StepId;
  label: string;
  /** Section is set up — shows a green tick, and its name says *, set up* straight after the label. */
  complete?: boolean;
  /**
   * Section was deliberately skipped (e.g. Equipment with no assets) — shows a grey tick, and its
   * name says *, skipped* (*, seen* on a placeholder step) straight after the label.
   */
  skipped?: boolean;
  /** Not selectable yet (the Launch item until everything is complete). */
  disabled?: boolean;
  /**
   * Why it is not selectable, shown as the button's title. Defaults to the Launch item's
   * "Complete every section to launch"; a post-launch stage says "Take the twin live first".
   */
  disabledReason?: string;
  /** The Launch item: drawn below a separator line. Its rocket is its `icon`. */
  isLaunch?: boolean;
  /**
   * A glyph in the marker instead of a number (`twin-details-page-shell`, R2.1): the rocket on
   * Launch, the map on Position on map (frame 03). A step with an icon takes no number, so the
   * steps before it keep theirs.
   */
  icon?: IconName;
  /**
   * Cannot be opened yet — every step after Twin details until the twin exists (frame 01,
   * `twin-details-page-shell` R2.2). The marker draws the lock and the step dims; the button is
   * unavailable (`aria-disabled`, still in the Tab order) and its name ends *, locked*. Wins over
   * `complete`, `skipped` and `trailing`.
   *
   * ⚠ Only the COLLAPSED list is drawn. The expanded row reuses the drawn marker and the dimmed
   * colour, built ahead of design by ruling (maria@twinlabs.ai, 2026-10-14) for Rob to ratify —
   * design-system epic §6B.
   */
  locked?: boolean;
  /** Why it is locked, shown as the button's title. Defaults to *Create the twin first*. */
  lockedReason?: string;
  /**
   * Holds its place and never blocks Launch — Position on map (frames 03 and 10, R2.3). Once
   * `skipped` is also set (seen), it draws the grey tick expanded, as a skipped step does, and a grey
   * badge on its marker while collapsed, which a skipped step alone does not.
   */
  placeholder?: boolean;
  /**
   * A short value after the label — the Floor Plan step's *85 %* (`floor-plan-page-checks`, R7).
   * Shown while the list is expanded; part of the button's name either way.
   */
  trailing?: string;
  /** What `trailing` is, said after it to assistive technology (*manufacturer identified*). */
  trailingWords?: string;
}

interface Props {
  className?: string;
  items: OnboardingNavItem[];
  /**
   * The step on screen. Null while the wizard is still deciding which step to open on
   * (`twin-details-page-landing`, R2): no step is marked current, and every step stays selectable.
   */
  current: StepId | null;
  onSelect: (id: StepId) => void;
  /**
   * Locks every section while Go Live is running. Activation replaces the twin's
   * whole dGraph model and can take minutes; letting the user wander off mid-flight
   * meant a completed activation could finish with nowhere to report itself, and
   * the plan could be edited underneath a run already committed to the old
   * snapshot. Closing the window is simpler than reporting after the fact.
   */
  locked?: boolean;
  /**
   * Shrunk to its numbers (`observers-page-stage`, FR4): the labels and ticks are hidden from sight
   * but stay in each button's accessible name, the label shows on hover, and a step that is set up
   * carries a small mint badge on its number. The host decides when; nothing here names a step.
   */
  collapsed?: boolean;
  /**
   * Shrinks or widens the list from its own foot (Observers page v2: *Collapse Steps*, and the
   * double chevron under the numbers when collapsed). Given only on a step whose list collapses;
   * without it there is no toggle.
   */
  onToggleCollapsed?: () => void;
}

/**
 * Left-hand onboarding navigation — freely clickable section list. Completed
 * sections get a green tick; the final "Launch Twin" item unlocks once every
 * section is set up. Everything locks while the twin is being activated.
 */
const OnboardingNav: React.FC<Props> = ({
  className,
  items,
  current,
  onSelect,
  locked = false,
  collapsed = false,
  onToggleCollapsed,
}) => {
  // Numbers count the steps without a glyph, so a glyph step never shifts the ones before it.
  let numbered = 0;
  return (
    <nav
      className={`${className ?? ''} ${collapsed ? 'collapsed' : ''}`}
      // Over the plan on the glass steps: a fly-to lands clear of it (`plan-fly-to`, R3).
      data-plan-cover="left"
      aria-busy={locked || undefined}
    >
      <SideTabs surface="on-glass" collapsed={collapsed}>
        {items.map(item => {
          const glyph: IconName | undefined = item.locked ? 'lock' : item.icon;
          const number = item.icon ? null : (numbered += 1);
          const badge = item.locked
            ? null
            : item.complete
              ? 'badge'
              : item.placeholder && item.skipped
                ? 'badge placeholder'
                : null;
          const unavailable = item.disabled || item.locked;
          // The step's state, in words as well as colour (colour is not a state): locked wins, then
          // set up, then a seen placeholder, then skipped. Said collapsed and expanded alike.
          const stateWords = item.locked
            ? ', locked'
            : item.complete
              ? ', set up'
              : item.placeholder && item.skipped
                ? ', seen'
                : item.skipped
                  ? ', skipped'
                  : null;
          return (
            <SideTab
              key={item.id}
              label={item.label}
              className={
                [item.isLaunch ? 'launch' : '', item.locked ? 'locked' : '']
                  .filter(Boolean)
                  .join(' ') || undefined
              }
              active={item.id === current && !item.locked}
              // Unavailable, never disabled: the step stays in the Tab order and is announced as
              // unavailable, and a press does nothing (card 13's accessibility rule).
              unavailable={locked || unavailable}
              title={
                locked
                  ? 'Activating the twin — please wait'
                  : item.locked
                    ? // Named as well as explained: collapsed, the lock replaces the number and the
                      // label is hidden, so the step's name is the only way to tell locks apart.
                      `${item.label}: ${item.lockedReason ?? 'Create the twin first'}`
                    : item.disabled
                      ? (item.disabledReason ?? 'Complete every section to launch')
                      : // The label ellipsises in the 240px nav ("Composite meas…"), so the full
                        // name is recoverable on hover; the accessible name is the full text anyway.
                        item.label
              }
              // The step on screen, said as well as shown — kept on a locked current step, whose
              // highlight is suppressed.
              aria-current={item.id === current ? 'step' : undefined}
              onClick={() => onSelect(item.id)}
              leading={
                <span className="marker">
                  {glyph ? <Icon name={glyph} size="sm" /> : number}
                  {collapsed && badge && <span className={badge} aria-hidden="true" />}
                </span>
              }
              trailing={
                <>
                  {stateWords && <span style={visuallyHidden}>{stateWords}</span>}
                  {item.trailing && !item.locked && (
                    <span className="trailing">
                      {/* Read after the label as ", 85 % manufacturer identified" whether drawn or not. */}
                      <span style={visuallyHidden}>{collapsed ? `, ${item.trailing}` : ', '}</span>
                      {collapsed ? null : (
                        <Tag surface="on-glass" size="dense" tone="neutral">
                          {item.trailing}
                        </Tag>
                      )}
                      {item.trailingWords && (
                        <span style={visuallyHidden}>{` ${item.trailingWords}`}</span>
                      )}
                    </span>
                  )}
                  {item.locked ? null : item.complete ? (
                    <span className="tick" title="Set up">
                      <Icon name="circle-check" variant="solid" size="md" />
                    </span>
                  ) : item.skipped ? (
                    <span className="tick skipped" title="Skipped">
                      <Icon name="circle-check" variant="solid" size="md" />
                    </span>
                  ) : null}
                </>
              }
            />
          );
        })}
      </SideTabs>
      {onToggleCollapsed && (
        <div className="foot">
          {/*
           * The same component in the same slot, unkeyed, in both states: React keeps the one
           * button node, so focus stays on it across the press.
           */}
          {collapsed ? (
            <Button
              type="button"
              variant="frosted"
              size="inline"
              iconOnly
              aria-label="Expand Steps"
              title="Expand Steps"
              iconLeft={<Icon name="angles-right" />}
              onClick={onToggleCollapsed}
            />
          ) : (
            <Button
              type="button"
              variant="frosted"
              size="inline"
              iconLeft={<Icon name="angles-left" />}
              onClick={onToggleCollapsed}
            >
              Collapse Steps
            </Button>
          )}
        </div>
      )}
    </nav>
  );
};

export default styled(OnboardingNav)`
  width: ${NAV_WIDTH_EXPANDED};
  flex-shrink: 0;
  /* A column, so the toggle sits at the foot of the list (v2). */
  display: flex;
  flex-direction: column;
  padding: ${space[4]} ${space[3]};
  transition: width ${motion.durMid} ${motion.easeUi};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  /*
   * The rows, their glass, the current step's frost and the collapsed clip are the design
   * system's side tabs (card 13). What stays here is the wizard's own: the markers, badges,
   * ticks, the Launch separator and the foot toggle.
   */

  /* Numbers only. The strip hides the label and tick from sight, not from the name. */
  &.collapsed {
    width: ${NAV_WIDTH_COLLAPSED};
    padding-left: ${space[2]};
    padding-right: ${space[2]};

    .foot > button {
      justify-content: center;
    }
  }

  /* The list's own toggle, pinned to the foot (Observers page v2). */
  .foot {
    margin-top: auto;
    padding-top: ${space[2]};

    > button {
      width: 100%;
      justify-content: flex-start;
    }
  }

  /* Launch sits below a separator, the strip's own on-glass divider. */
  .tl-tab.launch {
    margin-top: ${space[2]};
    border-top: 1px solid ${SIDE_TABS_GLASS_DIVIDER};
    border-top-left-radius: 0;
    padding-top: ${space[4]};
  }

  .marker {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    border: 1px solid currentColor;
    background-color: ${semantic.surface.base};
    font-size: ${typeRole.caption1.fontSize};
    line-height: ${typeRole.caption1.lineHeight};
    font-weight: ${weight.semibold};
  }

  /*
   * The current step's marker keeps its cyan fill. Card 13's "no cyan label on glass" rule is
   * about the label, which the strip draws in ink; the marker is drawn by the Twin details designs.
   */
  .tl-tab.is-active .marker {
    background-color: ${brand.cyan};
    border-color: ${brand.cyan};
    color: ${semantic.text.onSolidFeedback};
  }

  /* A set-up step's badge on its number, while collapsed (v3 design). */
  .badge {
    position: absolute;
    right: -2px;
    bottom: -2px;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${support.mint};
    /* The v3 design's 2px white border. */
    box-shadow: 0 0 0 2px ${surface[0]};
  }

  /* A seen placeholder step's badge: grey, as the design draws it (frame 03). */
  .badge.placeholder {
    background: ${semantic.text.placeholder};
  }

  .tick {
    display: inline-flex;
    flex-shrink: 0;
    /* The text-safe success ink: a verdict glyph standing on white, not ink on a mint fill. */
    color: ${semantic.text.success};
  }

  .tick.skipped {
    color: ${semantic.text.placeholder};
  }

  /* The step's short value (the Floor Plan score), between the label and the tick. */
  .trailing {
    display: inline-flex;
    flex-shrink: 0;
  }
`;
